import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SearchIcon from "@mui/icons-material/Search";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

const HOVER_CLOSE_DELAY = 180;

const FY_MONTH_ORDER = [
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
];

function normalizeOption(item) {
  if (item && typeof item === "object" && "value" in item) {
    return { val: item.value, label: item.label || String(item.value) };
  }
  if (item && typeof item === "object" && "id" in item) {
    return { val: item.id, label: item.name || String(item.id) };
  }
  return { val: item, label: String(item) };
}

function FilterPill({
  id,
  label,
  options = [],
  selected = [],
  onChange,
  activeDropdown,
  setActiveDropdown,
  icon,
  isSingle = false,
}) {
  const isOpen = activeDropdown === id;
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(20);
  const closeTimeoutRef = useRef(null);

  const safeSelected = Array.isArray(selected) ? selected : [];
  const hasSelections = safeSelected.length > 0;

  // 1. Convert selected array to a Set for O(1) ultra-fast lookups
  const selectedValuesSet = useMemo(() => {
    const set = new Set();
    safeSelected.forEach((item) => {
      const val = typeof item === "object" && item !== null ? item.value ?? item.id : item;
      set.add(String(val));
    });
    return set;
  }, [safeSelected]);

  useEffect(() => {
    if (!isOpen) setSearchQuery("");
    setVisibleCount(20);
  }, [isOpen, searchQuery]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setActiveDropdown(id);
  }, [id, setActiveDropdown]);

  const handleMouseLeave = useCallback(() => {
    closeTimeoutRef.current = setTimeout(() => {
      setActiveDropdown((prev) => (prev === id ? null : prev));
    }, HOVER_CLOSE_DELAY);
  }, [id, setActiveDropdown]);

  const handleToggleOption = (val) => {
    const valStr = String(val);
    const isAlreadySelected = selectedValuesSet.has(valStr);

    let nextSelected;
    if (isAlreadySelected) {
      nextSelected = safeSelected.filter((item) => {
        const itemVal = typeof item === "object" && item !== null ? item.value ?? item.id : item;
        return String(itemVal) !== valStr;
      });
    } else {
      const originalOption = options.find((opt) => {
        const { val: optVal } = normalizeOption(opt);
        return String(optVal) === valStr;
      });
      nextSelected = [...safeSelected, originalOption || val];
    }
    onChange(id, nextSelected);
  };

  // 2. Optimized filtering & sorting without deep array iterations
  const filteredList = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();

    const result = options.filter((item) => {
      if (!item) return false;
      if (!query) return true;
      const { label: itemLabel } = normalizeOption(item);
      return String(itemLabel).toLowerCase().includes(query);
    });

    return result.sort((a, b) => {
      const { val: valA, label: labelA } = normalizeOption(a);
      const { val: valB, label: labelB } = normalizeOption(b);

      const checkedA = selectedValuesSet.has(String(valA));
      const checkedB = selectedValuesSet.has(String(valB));

      if (checkedA && !checkedB) return -1;
      if (!checkedA && checkedB) return 1;

      return String(labelA).localeCompare(String(labelB), undefined, {
        sensitivity: "base",
        numeric: true,
      });
    });
  }, [options, searchQuery, selectedValuesSet]);

  const visibleList = useMemo(() => filteredList.slice(0, visibleCount), [filteredList, visibleCount]);

  // 4. Select All / Deselect All logic
  const isAllFilteredSelected = useMemo(() => {
    if (filteredList.length === 0) return false;
    return filteredList.every((item) => {
      const { val } = normalizeOption(item);
      return selectedValuesSet.has(String(val));
    });
  }, [filteredList, selectedValuesSet]);

  const handleSelectAllToggle = () => {
    if (isAllFilteredSelected) {
      // Remove all current filtered items from selection
      const filteredValsSet = new Set(
        filteredList.map((item) => String(normalizeOption(item).val))
      );
      const nextSelected = safeSelected.filter((item) => {
        const itemVal = typeof item === "object" && item !== null ? item.value ?? item.id : item;
        return !filteredValsSet.has(String(itemVal));
      });
      onChange(id, nextSelected);
    } else {
      // Add all filtered items to selection
      const existingValsSet = new Set(selectedValuesSet);
      const newItemsToAdd = [];

      filteredList.forEach((item) => {
        const { val } = normalizeOption(item);
        if (!existingValsSet.has(String(val))) {
          newItemsToAdd.push(item);
        }
      });

      onChange(id, [...safeSelected, ...newItemsToAdd]);
    }
  };

  return (
    <div className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <button
        type="button"
        onClick={() => setActiveDropdown(isOpen ? null : id)}
        aria-expanded={isOpen}
        className={`flex items-center gap-1.5 h-8 px-3 rounded-xl text-[11px] font-bold border transition-all duration-150 ${hasSelections
          ? "bg-[#0a2540] border-[#0a2540] text-white shadow-md"
          : "bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50"
          }`}
      >
        {icon && <span className={hasSelections ? "text-white/70" : "text-slate-400"}>{icon}</span>}
        <span>{label}</span>
        {hasSelections ? (
          <span className="bg-white/20 text-white text-[10px] font-extrabold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
            {safeSelected.length}
          </span>
        ) : (
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 13,
              opacity: 0.5,
              transform: isOpen ? "rotate(180deg)" : "none",
              transition: "transform 0.15s ease",
            }}
          />
        )}
      </button>

      {isOpen && (
        <>
          <div className="absolute top-full left-0 h-1.5 w-full" />
          <div className="absolute top-full left-0 mt-1.5 w-60 bg-white border border-slate-200 rounded-2xl shadow-xl p-1.5 flex flex-col gap-0.5 z-50 animate-[fadeIn_0.12s_ease-out]">
            {/* Search Input */}
            <div className="relative flex items-center mb-1 px-1">
              <SearchIcon className="absolute left-2.5 text-slate-400" sx={{ fontSize: 13 }} />
              <input
                type="text"
                placeholder={`Search ${label}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-7 pl-7 pr-2.5 bg-slate-50 border border-slate-100 rounded-lg text-[11px] text-slate-800 placeholder-slate-400 focus:outline-none focus:border-slate-200 focus:bg-white transition-all"
              />
            </div>

            {/* Select All Header Bar */}
            {/* {filteredList.length > 0 && (
              <div
                onClick={handleSelectAllToggle}
                className="flex items-center justify-between px-2.5 py-1.5 mb-1 rounded-lg text-[10px] font-bold bg-slate-100/70 text-slate-700 cursor-pointer hover:bg-slate-200/70 transition-colors"
              >
                <div className="flex items-center gap-1.5">
                  {isAllFilteredSelected ? (
                    <CheckBoxIcon sx={{ fontSize: 14, color: "#0a2540" }} />
                  ) : (
                    <CheckBoxOutlineBlankIcon sx={{ fontSize: 14, color: "#cbd5e1" }} />
                  )}
                  <span>{isAllFilteredSelected ? "Deselect All" : "Select All"}</span>
                </div>
                <span className="text-[9px] text-slate-400 font-normal">
                  ({filteredList.length})
                </span>
              </div>
            )} */}

            {!isSingle && filteredList.length > 0 && (
              <div
                onClick={handleSelectAllToggle}
                className="flex items-center justify-between px-2.5 py-1.5 mb-1 rounded-lg text-[10px] font-bold bg-slate-100/70 text-slate-700 cursor-pointer hover:bg-slate-200/70 transition-colors"
              >
                <div className="flex items-center gap-1.5">
                  {isAllFilteredSelected ? (
                    <CheckBoxIcon sx={{ fontSize: 14, color: "#0a2540" }} />
                  ) : (
                    <CheckBoxOutlineBlankIcon sx={{ fontSize: 14, color: "#cbd5e1" }} />
                  )}
                  <span>{isAllFilteredSelected ? "Deselect All" : "Select All"}</span>
                </div>
                <span className="text-[9px] text-slate-400 font-normal">
                  ({filteredList.length})
                </span>
              </div>
            )}

            {/* Options List */}
            <div 
              className="max-h-48 overflow-y-auto flex flex-col gap-0.5 custom-scrollbar"
              onScroll={(e) => {
                const { scrollHeight, scrollTop, clientHeight } = e.target;
                if (scrollHeight - scrollTop <= clientHeight + 1.5 && visibleCount < filteredList.length) {
                  setVisibleCount(prev => prev + 20);
                }
              }}
            >
              {visibleList.length > 0 ? (
                visibleList.map((item) => {
                  const { val, label: itemLabel } = normalizeOption(item);
                  const checked = selectedValuesSet.has(String(val));

                  return (
                    <div
                      key={val}
                      onClick={() => handleToggleOption(val)}
                      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-xl text-[11px] font-semibold cursor-pointer transition-colors ${checked ? "bg-slate-50 text-slate-900" : "text-slate-500 hover:bg-slate-50"
                        }`}
                    >
                      {isSingle ? (
                        checked ? (
                          <RadioButtonCheckedIcon sx={{ fontSize: 14, color: "#0a2540" }} />
                        ) : (
                          <RadioButtonUncheckedIcon sx={{ fontSize: 14, color: "#cbd5e1" }} />
                        )
                      ) : checked ? (
                        <CheckBoxIcon sx={{ fontSize: 14, color: "#0a2540" }} />
                      ) : (
                        <CheckBoxOutlineBlankIcon sx={{ fontSize: 14, color: "#cbd5e1" }} />
                      )}
                      <span className={`truncate ${checked ? "font-bold text-slate-900" : ""}`}>
                        {itemLabel}
                      </span>
                    </div>
                  );
                })
              ) : (
                <div className="text-[10px] text-slate-400 text-center py-3 font-medium">
                  No results found
                </div>
              )}

              {/* Indicator if dataset exceeds total options, now hidden since we use infinite scroll */}
              {visibleCount < filteredList.length && (
                <div className="text-[9px] text-slate-400 text-center py-2 font-medium bg-slate-50 rounded-md">
                  Scroll for more...
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function FYMonthPicker({ selectedMonths = [], onChange, activeDropdown, setActiveDropdown }) {
  const dropdownId = "fy_months_dropdown";
  const isOpen = activeDropdown === dropdownId;
  const closeTimeoutRef = useRef(null);

  const safeMonths = Array.isArray(selectedMonths) ? selectedMonths.map(Number) : [];

  const rangeLabel = useMemo(() => {
    if (safeMonths.length === 12) return "Full FY";
    if (safeMonths.length === 0) return "Select Months";

    const matched = FY_MONTH_ORDER.filter((m) => safeMonths.includes(m.value));
    if (matched.length === 1) return matched[0].label.slice(0, 3);
    return `${matched[0]?.label.slice(0, 3)} (+${safeMonths.length - 1})`;
  }, [safeMonths]);

  const toggleMonth = (mVal) => {
    const updated = safeMonths.includes(mVal)
      ? safeMonths.filter((m) => m !== mVal)
      : [...safeMonths, mVal];
    onChange(updated);
  };

  return (
    <div
      className="relative"
      onMouseEnter={() => {
        if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
        setActiveDropdown(dropdownId);
      }}
      onMouseLeave={() => {
        closeTimeoutRef.current = setTimeout(() => setActiveDropdown(null), HOVER_CLOSE_DELAY);
      }}
    >
      <button
        type="button"
        onClick={() => setActiveDropdown(isOpen ? null : dropdownId)}
        className={`flex items-center gap-1.5 h-8 px-3 rounded-xl text-[11px] font-bold border transition-all duration-150 ${safeMonths.length > 0
          ? "bg-[#0a2540] border-[#0a2540] text-white shadow-md"
          : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
          }`}
      >
        <CalendarMonthIcon sx={{ fontSize: 13 }} />
        <span>{rangeLabel}</span>
        <KeyboardArrowDownIcon
          sx={{
            fontSize: 13,
            transform: isOpen ? "rotate(180deg)" : "none",
            transition: "transform 0.15s",
          }}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-1.5 w-48 bg-white border border-slate-200 rounded-2xl shadow-xl p-1.5 z-50 flex flex-col gap-0.5 animate-[fadeIn_0.12s_ease-out]">
          <div className="text-[9px] uppercase tracking-wider font-extrabold text-slate-400 px-2 py-1">
            Months (FY Order)
          </div>

          <div
      onClick={() => {
        if (safeMonths.length === FY_MONTH_ORDER.length) {
          onChange([]);
        } else {
          onChange(FY_MONTH_ORDER.map((m) => m.value));
        }
      }}
      className="flex items-center justify-between px-2.5 py-1.5 mb-1 rounded-lg text-[10px] font-bold bg-slate-100/70 text-slate-700 cursor-pointer hover:bg-slate-200/70 transition-colors"
    >
      <div className="flex items-center gap-1.5">
        {safeMonths.length === FY_MONTH_ORDER.length ? (
          <CheckBoxIcon sx={{ fontSize: 14, color: "#0a2540" }} />
        ) : (
          <CheckBoxOutlineBlankIcon sx={{ fontSize: 14, color: "#cbd5e1" }} />
        )}
        <span>
          {safeMonths.length === FY_MONTH_ORDER.length ? "Deselect All" : "Select All"}
        </span>
      </div>
      <span className="text-[9px] text-slate-400 font-normal">
        ({FY_MONTH_ORDER.length})
      </span>
    </div>
          <div className="max-h-56 overflow-y-auto custom-scrollbar flex flex-col gap-0.5">
            {FY_MONTH_ORDER.map((m) => {
              const checked = safeMonths.includes(m.value);
              return (
                <div
                  key={m.value}
                  onClick={() => toggleMonth(m.value)}
                  className={`flex items-center gap-2 px-2.5 py-1.5 rounded-xl text-[11px] font-semibold cursor-pointer transition-colors ${checked ? "bg-slate-50 text-slate-900" : "text-slate-500 hover:bg-slate-50"
                    }`}
                >
                  {checked ? (
                    <CheckBoxIcon sx={{ fontSize: 14, color: "#0a2540" }} />
                  ) : (
                    <CheckBoxOutlineBlankIcon sx={{ fontSize: 14, color: "#cbd5e1" }} />
                  )}
                  <span className={checked ? "font-bold" : ""}>{m.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function AppliedFiltersStrip({ filterConfigs, onFilterChange, selectedMonths, onMonthChange, onClearAll, actions }) {
  const groups = useMemo(() => {
    const list = [];

    filterConfigs.forEach((config) => {
      const selected = config.selected || [];
      if (!Array.isArray(selected) || selected.length === 0) return;

      const chips = selected.map((item) => {
        const { val, label } = normalizeOption(item);
        return { val, label };
      });

      list.push({
        id: config.id,
        label: config.label,
        chips,
      });
    });

    if (Array.isArray(selectedMonths) && selectedMonths.length > 0 && selectedMonths.length < 12) {
      const sortedMonths = FY_MONTH_ORDER.filter((m) => selectedMonths.map(Number).includes(m.value));
      list.push({
        id: "months",
        label: "Months",
        chips: sortedMonths.map((m) => ({
          val: m.value,
          label: m.label.slice(0, 3),
        })),
      });
    }

    return list;
  }, [filterConfigs, selectedMonths]);

  const totalChips = groups.reduce((acc, g) => acc + g.chips.length, 0);
  // if (totalChips === 0) return null;

  const handleRemoveChip = (groupId, chipVal) => {
    if (groupId === "months") {
      onMonthChange(selectedMonths.filter((m) => Number(m) !== Number(chipVal)));
      return;
    }

    const config = filterConfigs.find((c) => c.id === groupId);
    if (!config) return;

    const updated = (config.selected || []).filter((item) => {
      const { val } = normalizeOption(item);
      return String(val) !== String(chipVal);
    });

    onFilterChange(groupId, updated);
  };

  return (
    <div className=" rounded-b-xl  px-6 py-2">
      <div className="max-w-[1600px] mx-auto flex flex-wrap items-center gap-x-4 gap-y-2">

        {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
        {totalChips > 0 && (
          <>
            {actions && <span className="h-4 w-px bg-slate-200 shrink-0" />}
            <div className="flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-wider text-slate-400 shrink-0">
              <TuneRoundedIcon sx={{ fontSize: 13 }} />
              Applied
            </div>

            {groups.map((group, gIdx) => (
              <React.Fragment key={group.id}>
                {gIdx > 0 && <span className="h-4 w-px bg-slate-200 shrink-0" />}
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[10px] font-bold text-slate-400 shrink-0">{group.label}</span>
                  {group.chips.map((chip) => (
                    <span
                      key={`${group.id}-${chip.val}`}
                      className="group flex items-center gap-1 h-6 pl-2.5 pr-1.5 rounded-full text-[11px] font-semibold text-slate-700 bg-white border border-slate-200 shadow-sm transition-colors"
                    >
                      {chip.label}
                      <button
                        type="button"
                        onClick={() => handleRemoveChip(group.id, chip.val)}
                        aria-label={`Remove ${chip.label}`}
                        className="flex items-center justify-center w-3.5 h-3.5 rounded-full text-slate-400 hover:text-white hover:bg-rose-500 transition-colors"
                      >
                        <CloseRoundedIcon sx={{ fontSize: 11 }} />
                      </button>
                    </span>
                  ))}
                </div>
              </React.Fragment>
            ))}

            <button
              type="button"
              onClick={onClearAll}
              className="ml-auto text-[10px] font-bold text-slate-400 hover:text-rose-600 transition-colors shrink-0"
            >
              Clear all
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function FilterBar2({
  filters = [],
  onFilterChange,
  selectedMonth,
  setSelectedMonth,
  searchTerm,
  searchPlaceHolder,
  setSearchTerm,
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  selectedFromDate,
  setSelectedFromDate,
  selectedToDate,
  setSelectedToDate,
  onClearFilters,
  actions,
  searchWidth,
  viewControls,
}) {
  const [activeDropdown, setActiveDropdown] = useState(null);

  return (
    <section
      className="sticky top-0 z-30 border-b border-slate-200/80 rounded-xl mb-4"
      style={{
        background: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(12px)",
        boxShadow: "0 1px 3px rgba(10,37,64,0.05)",
      }}
    >
      <div className="max-w-[1600px] mx-auto px-6 py-3 flex flex-wrap gap-2.5 items-center">
        {/* Dynamic Multi-Select Filters */}
        {filters.map((f) => (
          <FilterPill
            key={f.id}
            id={f.id}
            label={f.label}
            options={f.options}
            selected={f.selected}
            onChange={onFilterChange}
            activeDropdown={activeDropdown}
            setActiveDropdown={setActiveDropdown}
            isSingle={f.isSingle}
            icon={f.icon}
          />
        ))}

        {/* Month Picker */}
        {selectedMonth !== undefined && (
          <FYMonthPicker
            selectedMonths={selectedMonth}
            onChange={setSelectedMonth}
            activeDropdown={activeDropdown}
            setActiveDropdown={setActiveDropdown}
          />
        )}

        {/* Table Date Range */}
        {(selectedFromDate !== undefined || selectedToDate !== undefined) && (
          <div className="flex items-center gap-1 bg-slate-100/80 p-1 rounded-xl border border-slate-200 text-[11px]">
            <span className="text-[10px] font-bold text-slate-500 px-1">Table:</span>
            <input
              type="date"
              value={selectedFromDate || ""}
              onChange={(e) => setSelectedFromDate(e.target.value)}
              className="h-7 px-1.5 bg-white border border-slate-200 rounded-lg text-slate-700 text-[11px] focus:outline-none"
            />
            <span className="text-slate-400 font-bold">-</span>
            <input
              type="date"
              value={selectedToDate || ""}
              onChange={(e) => setSelectedToDate(e.target.value)}
              className="h-7 px-1.5 bg-white border border-slate-200 rounded-lg text-slate-700 text-[11px] focus:outline-none"
            />
          </div>
        )}

        {/* Search Input */}
        {searchTerm !== undefined && (
          <div 
            className="relative flex items-center"
            style={{ minWidth: searchWidth || "300px" }}
          >
            <SearchIcon className="absolute left-2.5 text-slate-400" sx={{ fontSize: 14 }} />
            <input
              type="text"
              placeholder={searchPlaceHolder || "Search..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full h-8 pl-8 pr-2.5 bg-white border border-slate-200 rounded-xl text-[11px] text-slate-800 placeholder-slate-400 focus:outline-none focus:border-slate-300 shadow-sm"
            />
          </div>
        )}

        {/* View Controls Group (Fieldset with Legend) */}
        {viewControls && (
          <fieldset className="border border-slate-200 rounded-xl px-3 py-1 flex items-center gap-2 ml-auto shrink-0 bg-white">
            <legend className="text-[9px] font-extrabold text-slate-400 uppercase tracking-wider px-1.5 bg-white select-none">
              View & Actions
            </legend>
            <div className="flex items-center gap-1.5">
              {viewControls}
            </div>
          </fieldset>
        )}

        {/* Dump Date Range */}
        {(fromDate !== undefined || toDate !== undefined) && (
          <div className="flex items-center gap-1 bg-slate-100/80 p-1 rounded-xl border border-slate-200 text-[11px]">
            <span className="text-[10px] font-bold text-slate-500 px-1">Dump:</span>
            <input
              type="date"
              value={fromDate || ""}
              onChange={(e) => setFromDate(e.target.value)}
              className="h-7 px-1.5 bg-white border border-slate-200 rounded-lg text-slate-700 text-[11px] focus:outline-none"
            />
            <span className="text-slate-400 font-bold">-</span>
            <input
              type="date"
              value={toDate || ""}
              onChange={(e) => setToDate(e.target.value)}
              className="h-7 px-1.5 bg-white border border-slate-200 rounded-lg text-slate-700 text-[11px] focus:outline-none"
            />
          </div>
        )}

        {/* <div className="flex items-center gap-2 ml-auto shrink-0">
          {onClearFilters && (
            <button
              type="button"
              onClick={onClearFilters}
              title="Clear all filters"
              className="h-8 px-2.5 flex items-center gap-1 text-[11px] font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 rounded-xl border border-rose-100 transition-all"
            >
              <BlockOutlinedIcon sx={{ fontSize: 13 }} /> Clear
            </button>
          )}
        </div> */}
      </div>

      <AppliedFiltersStrip
        filterConfigs={filters}
        onFilterChange={onFilterChange}
        selectedMonths={selectedMonth}
        onMonthChange={setSelectedMonth}
        onClearAll={onClearFilters}
        actions={actions}
      />
    </section>
  );
}